// frontend/src/hooks/useDataHealthData.js
//
// TanStack-backed data hooks for the Data Health page. Currently just
// the summary fetch; per-issue detail fetches stay inline on the page
// because they're paginated drill-down queries with their own UI state
// machine.

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { sessionApi } from '../services/SessionApiService';
import { useAuth } from '../contexts/AuthContext';
import { dataHealthSummary as dataHealthSummaryKey } from '../queries/keys';
import { readResult } from '../queries/api';

const THIRTY_SEC = 30 * 1000;

/**
 * Full Data Health summary — counts per issue category, recent fixes,
 * milestone progress, etc. The bell-icon badge in TopMenu uses a
 * separate, slimmer endpoint (contexts/NotificationContext useNotifications);
 * this is the page-level data.
 *
 * Cached briefly. After inline-fix events the page invalidates this
 * query (caller passes the returned `refetch` to debounce-then-refresh
 * logic).
 */
export function useDataHealthSummary() {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const query = useQuery({
    queryKey: dataHealthSummaryKey.all(storeId),
    queryFn: async () => {
      const res = await sessionApi.get('/api/data-health/summary/');
      if (!res?.ok) throw new Error(`Server returned ${res?.status || 'unknown'}`);
      return res.json();
    },
    staleTime: THIRTY_SEC,
    enabled: !!storeId,
  });
  return readResult(query, t);
}
